import { Link, useRouterState } from "@tanstack/react-router";
import {
  MessageSquare, Boxes, Network, GitPullRequestArrow, BookOpen,
  Plug, LayoutGrid, History, Bookmark, Settings,
} from "lucide-react";
import logo from "@/assets/logo-color.png";
import { useCompanion } from "@/state/CompanionContext";

type Item = { label: string; to: string; icon: React.ComponentType<{ className?: string }> };

const sections: { label: string; items: Item[] }[] = [
  {
    label: "Engineering",
    items: [
      { label: "Copilot", to: "/copilot", icon: MessageSquare },
      { label: "Parts Catalog", to: "/parts", icon: Boxes },
      { label: "BOM Explorer", to: "/bom", icon: Network },
      { label: "Changes", to: "/changes", icon: GitPullRequestArrow },
      { label: "Knowledge Base", to: "/knowledge", icon: BookOpen },
    ],
  },
  {
    label: "Platform",
    items: [
      { label: "Teamcenter Integration", to: "/integration", icon: Plug },
      { label: "Integrations Hub", to: "/hub", icon: LayoutGrid },
      { label: "Actions Audit Log", to: "/audit", icon: History },
    ],
  },
  {
    label: "Personal",
    items: [
      { label: "Saved Insights", to: "/insights", icon: Bookmark },
      { label: "Settings", to: "/settings", icon: Settings },
    ],
  },
];

export function AppSidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { goHome } = useCompanion();

  return (
    <aside className="flex h-screen w-[220px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
      {/* Brand */}
      <div className="flex flex-col items-center gap-2 px-4 pt-5 pb-4 border-b border-sidebar-border">
        <img
          src={logo}
          alt="Joules to Watts"
          className="h-14 w-auto object-contain mix-blend-screen"
        />
        <div className="text-[13px] font-semibold tracking-tight text-white text-center">
          PLM Copilot
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section, i) => (
          <div key={section.label} className={i > 0 ? "mt-4 pt-4 border-t border-sidebar-border" : ""}>
            <div className="px-2 pb-1.5 text-[10.5px] font-medium uppercase tracking-wider text-sidebar-muted">
              {section.label}
            </div>
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = pathname === item.to || (item.to === "/copilot" && pathname === "/");
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={item.to === "/copilot" ? () => goHome() : undefined}
                      className={`relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-[13px] transition-colors duration-150 ${
                        active
                          ? "bg-sidebar-active text-white font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-hover hover:text-white"
                      }`}
                    >
                      {active && (
                        <span className="absolute left-0 top-1 bottom-1 w-[2px] rounded-full bg-primary" />
                      )}
                      <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Identity */}
      <div className="flex items-center gap-2.5 border-t border-sidebar-border px-3 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-[11px] font-medium text-primary-foreground">
          AC
        </div>
        <div className="min-w-0">
          <div className="truncate text-[13px] font-medium text-sidebar-foreground">Alex Carter</div>
          <div className="truncate text-[11px] text-sidebar-muted">Avantek · Structures</div>
        </div>
      </div>
    </aside>
  );
}
